import { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router";
import axios from 'axios';

import { Status, Button } from "../components/component.js";
import { CheckBadge, CheckCircle, ChevronLeft, ChevronRight, TimeClock } from "../../files/icons/index.js";
import Helmet from "../components/helmet.js";
import { DateSelector } from "../components/datePicker.js";

import { AppContext } from '../../context.js';

import PatientCard from "../components/patientCard.js";
import TaskCard from "../components/taskCard.js";
import AppointmentCard from "../components/appointmentCard.js";
import AssessmentCard from "../components/assessmentCard.js";
import NavigatorMetrics from "../components/navigatorMetrics.js";

import { FirstDayOfMonth, LastDayOfMonth, InRangeUTC } from "../../utils/datetime.js";

function Dashboard() {
  const context = useContext(AppContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);

  const [currentPeriodDateStart, setCurrentPeriodDateStart] = useState(null);
  const [currentPeriodDateEnd, setCurrentPeriodDateEnd] = useState(null);

  const [activeView, setActiveView] = useState("table"); // table, month
  const [monthViewDate, setMonthViewDate] = useState(new Date());
  const [monthViewTab, setMonthViewTab] = useState("tasks"); // tasks, appointments, assessments

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "projects");
  const [deadlineFilter, setDeadlineFilter] = useState("all"); // all, coming-soon, delayed
  const [patientPhaseFilter, setPatientPhaseFilter] = useState("pre-aligned"); // pre-aligned, post-aligned, unaligned

  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/dashboard', {
        userID: context.id,
      }),
      axios.get('/api/admin/settings', {
      }),
      axios.post('/api/admin/physicians', {
        filter: {}
      }),
    ]).then(axios.spread((dashboard, settings, physicians) => {
      setProjects(dashboard.data.projects || []);
      setTasks(dashboard.data.tasks || []);
      setAppointments(dashboard.data.appointments || []);
      setAssessments(dashboard.data.assessments || []);
      setSettings(settings.data);
      setPhysicians(physicians.data.physicians || []);
      setPatients(dashboard.data.patients || []);
      setCurrentPeriodDateStart(FirstDayOfMonth(new Date()));
      setCurrentPeriodDateEnd(LastDayOfMonth(new Date()));
      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function switchTab(tab) {
    setActiveTab(tab);
    setSearchParams({ tab });
  }

  // Helper function to calculate deadline warnings
  function calculateDeadlineWarnings(items) {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const activeItems = items.filter(
      (item) => item.statusType !== "COMPLETED" && item.statusType !== "CANCELED"
    );

    const delayed = activeItems.filter((item) => {
      if (!item.deadlineAt) return false;
      const deadline = new Date(item.deadlineAt);
      return deadline < now;
    }).length;

    const comingSoon = activeItems.filter((item) => {
      if (!item.deadlineAt) return false;
      const deadline = new Date(item.deadlineAt);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;

    return { delayed, comingSoon };
  }

  // Helper function to get filter counts for current active tab
  function getFilterCounts() {
    let items = [];
    if (activeTab === "tasks") items = tasks;
    else if (activeTab === "appointments") items = appointments;
    else if (activeTab === "assessments") items = assessments;
    else return { all: 0, comingSoon: 0, delayed: 0 };

    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const activeItems = items.filter(
      (item) => item.statusType !== "COMPLETED" && item.statusType !== "CANCELED"
    );

    const delayed = activeItems.filter((item) => {
      if (!item.deadlineAt) return false;
      const deadline = new Date(item.deadlineAt);
      return deadline < now;
    }).length;

    const comingSoon = activeItems.filter((item) => {
      if (!item.deadlineAt) return false;
      const deadline = new Date(item.deadlineAt);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;

    return { all: activeItems.length, comingSoon, delayed };
  }

  // Summary
  function renderProjectsSummaryCard() {
    const activeProjects = projects.filter((project) => project.statusType !== "COMPLETED" && project.statusType !== "CANCELED" && project.userID === context.id);

    return (
      <div className={"card clickable w-25 me-2 " + (activeTab === "projects" ? "blue" : "")} onClick={() => switchTab("projects")}>
        <div className="card-content sm">
          <div className="typo-strong-300">{activeProjects.length}</div>
          <div className="typo-normal-175">Patients</div>
        </div>
      </div>
    );
  }

  function renderTasksSummaryCard() {
    const activeTasks = tasks.filter((task) => task.statusType !== "COMPLETED" && task.statusType !== "CANCELED");
    const { delayed, comingSoon } = calculateDeadlineWarnings(tasks);

    return (
      <div className={"card clickable w-25 me-2 " + (activeTab === "tasks" ? "blue" : "")} onClick={() => switchTab("tasks")}>
        <div className="card-content sm">
          <div className="d-flex align-items-end">
            <div style={{ width: "50%" }}>
              <div className="typo-strong-300">{activeTasks.length}</div>
              <div className="typo-normal-175">Tasks</div>
            </div>
            <div className="text-end" style={{ width: "50%" }}>
              <div className="typo-normal-150">Delayed: {delayed}</div>
              <div className="typo-normal-150">Coming soon: {comingSoon}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderAppointmentsSummaryCard() {
    const activeAppointments = appointments.filter((appointment) => appointment.statusType !== "COMPLETED" && appointment.statusType !== "CANCELED");
    const { delayed, comingSoon } = calculateDeadlineWarnings(appointments);

    return (
      <div className={"card clickable w-25 me-2 " + (activeTab === "appointments" ? "blue" : "")} onClick={() => switchTab("appointments")}>
        <div className="card-content sm">
          <div className="d-flex align-items-end">
            <div style={{ width: "50%" }}>
              <div className="typo-strong-300">{activeAppointments.length}</div>
              <div className="typo-normal-175">Appointments</div>
            </div>
            <div className="text-end" style={{ width: "50%" }}>
              <div className="typo-normal-150">Delayed: {delayed}</div>
              <div className="typo-normal-150">Coming soon: {comingSoon}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderAssessmentsSummaryCard() {
    const activeAssessments = assessments.filter((assessment) => assessment.statusType !== "COMPLETED" && assessment.statusType !== "CANCELED");
    const { delayed, comingSoon } = calculateDeadlineWarnings(assessments);

    return (
      <div className={"card clickable w-25 " + (activeTab === "assessments" ? "blue" : "")} onClick={() => switchTab("assessments")}>
        <div className="card-content sm">
          <div className="d-flex align-items-end">
            <div style={{ width: "50%" }}>
              <div className="typo-strong-300">{activeAssessments.length}</div>
              <div className="typo-normal-175">Assessments</div>
            </div>
            <div className="text-end" style={{ width: "50%" }}>
              <div className="typo-normal-150">Delayed: {delayed}</div>
              <div className="typo-normal-150">Coming soon: {comingSoon}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter buttons
  function renderFilterButtons() {
    if (activeTab === "projects") {
      const activeProjects = projects.filter(p => p.statusType !== "COMPLETED" && p.statusType !== "CANCELED" && p.userID === context.id);
      const patientMap = new Map(patients.map(p => [p.id, p]));

      const preCount = activeProjects.filter(p => patientMap.get(p.patientID)?.lifecycleStage === "PRE_ALIGNMENT").length;
      const postCount = activeProjects.filter(p => {
        const stage = patientMap.get(p.patientID)?.lifecycleStage;
        return stage === "POST_ALIGNMENT_NEW" || stage === "POST_ALIGNMENT_EST";
      }).length;
      const unalignedCount = activeProjects.filter(p => patientMap.get(p.patientID)?.lifecycleStage === "UNALIGNED").length;

      return (
        <div className="d-flex">
          <div className={"item grey typo-normal-150 me-2 " + (patientPhaseFilter === "pre-aligned" ? "blue" : "")} onClick={() => setPatientPhaseFilter("pre-aligned")}>
            <span className="item-content">Pre-Alignment</span>
            <span className="item-counter">{preCount}</span>
          </div>
          <div className={"item grey typo-normal-150 me-2 " + (patientPhaseFilter === "post-aligned" ? "blue" : "")} onClick={() => setPatientPhaseFilter("post-aligned")}>
            <span className="item-content">Post-Alignment</span>
            <span className="item-counter">{postCount}</span>
          </div>
          <div className={"item grey typo-normal-150 " + (patientPhaseFilter === "unaligned" ? "blue" : "")} onClick={() => setPatientPhaseFilter("unaligned")}>
            <span className="item-content">Unaligned</span>
            <span className="item-counter">{unalignedCount}</span>
          </div>
        </div>
      );
    }

    const counts = getFilterCounts();

    return (
      <div className="d-flex">
        <div className={"item grey typo-normal-150 me-2 " + (deadlineFilter === "all" ? "blue" : "")} onClick={() => setDeadlineFilter("all")}>
          <span className="item-content">All</span>
          <span className="item-counter">{counts.all}</span>
        </div>
        <div className={"item grey typo-normal-150 me-2 " + (deadlineFilter === "coming-soon" ? "blue" : "")} onClick={() => setDeadlineFilter("coming-soon")}>
          <span className="item-content">Coming soon</span>
          <span className="item-counter">{counts.comingSoon}</span>
        </div>
        <div className={"item grey typo-normal-150 " + (deadlineFilter === "delayed" ? "blue" : "")} onClick={() => setDeadlineFilter("delayed")}>
          <span className="item-content">Delayed</span>
          <span className="item-counter">{counts.delayed}</span>
        </div>
      </div>
    );
  }

  // Lists
  function renderProjects() {
    let activeProjects = projects.filter((project) => project.statusType !== "COMPLETED" && project.statusType !== "CANCELED" && project.userID === context.id);

    if (patientPhaseFilter !== "all") {
      const patientMap = new Map(patients.map(p => [p.id, p]));
      activeProjects = activeProjects.filter(project => {
        const stage = patientMap.get(project.patientID)?.lifecycleStage;
        if (patientPhaseFilter === "pre-aligned") return stage === "PRE_ALIGNMENT";
        if (patientPhaseFilter === "post-aligned") return stage === "POST_ALIGNMENT_NEW" || stage === "POST_ALIGNMENT_EST";
        if (patientPhaseFilter === "unaligned") return stage === "UNALIGNED";
        return true;
      });
    }

    if (activeProjects.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No activities</div>
        </div>
      );
    }

    return activeProjects.map((project) => {
      const patient = patients.find(p => p.id === project.patientID);
      if (!patient) return null;

      return (
        <PatientCard
          key={project.id}
          patient={patient}
          link={true}
          activeProject={project}
        />
      );
    });
  }

  function renderTasks(activeTasks) {
    // Apply deadline filter
    if (deadlineFilter === "delayed") {
      const now = new Date();
      activeTasks = activeTasks.filter((task) => {
        if (!task.deadlineAt) return false;
        const deadline = new Date(task.deadlineAt);
        return deadline < now;
      });
    } else if (deadlineFilter === "coming-soon") {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      activeTasks = activeTasks.filter((task) => {
        if (!task.deadlineAt) return false;
        const deadline = new Date(task.deadlineAt);
        return deadline >= now && deadline <= sevenDaysFromNow;
      });
    }

    if (activeTasks.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No tasks</div>
        </div>
      );
    }

    // Group tasks by projectID
    const tasksByProject = activeTasks.reduce((acc, task) => {
      const projectID = task.projectID;
      if (!acc[projectID]) acc[projectID] = [];
      acc[projectID].push(task);
      return acc;
    }, {});

    // Create project lookup map from unique projectIDs in tasks
    const uniqueProjectIDs = Object.keys(tasksByProject);
    const projectMap = new Map();
    uniqueProjectIDs.forEach(projectID => {
      const project = projects.find(p => p.id === projectID);
      if (project) {
        projectMap.set(projectID, project);
      }
    });

    // Render with compact PatientCard dividers
    return Object.entries(tasksByProject).map(([projectID, projectTasks]) => {
      const project = projectMap.get(projectID);
      const patient = project ? patients.find(p => p.id === project.patientID) : null;

      // Sort tasks by number
      const sortedTasks = projectTasks.sort((a, b) => a.number - b.number);

      return (
        <div key={"project_" + projectID}>
          {patient && (
            <PatientCard
              patient={patient}
              link={true}
              compact={true}
              activeProject={project}
            />
          )}
          {sortedTasks.map((task) => {
            const projectTemplate = settings.projectTemplates.find((pt) =>
              pt.taskTemplates.some((tt) => tt.id === task.templateID)
            );
            return (
              <TaskCard
                key={task.id}
                task={task}
                users={physicians}
                settings={settings}
                projectTemplate={projectTemplate}
                onTaskDuplicated={(duplicatedTask) => setTasks((prev) => [...prev, duplicatedTask])}
                onTaskDeleted={(deletedID) => setTasks((prev) => prev.filter((t) => t.id !== deletedID))}
              />
            );
          })}
          <div className="pb-3"></div>
          <div className="pb-3"></div>
        </div>
      );
    });
  }

  function renderAppointments(activeAppointments) {
    // Apply deadline filter
    if (deadlineFilter === "delayed") {
      const now = new Date();
      activeAppointments = activeAppointments.filter((appointment) => {
        if (!appointment.deadlineAt) return false;
        const deadline = new Date(appointment.deadlineAt);
        return deadline < now;
      });
    } else if (deadlineFilter === "coming-soon") {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      activeAppointments = activeAppointments.filter((appointment) => {
        if (!appointment.deadlineAt) return false;
        const deadline = new Date(appointment.deadlineAt);
        return deadline >= now && deadline <= sevenDaysFromNow;
      });
    }

    if (activeAppointments.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No appointments</div>
        </div>
      );
    }

    // Group appointments by patientID
    const appointmentsByPatient = activeAppointments.reduce((acc, appointment) => {
      const patientID = appointment.patientID;
      if (!acc[patientID]) acc[patientID] = [];
      acc[patientID].push(appointment);
      return acc;
    }, {});

    // Create patient lookup map from unique patientIDs in appointments
    const uniquePatientIDs = Object.keys(appointmentsByPatient);
    const patientMap = new Map();
    uniquePatientIDs.forEach(patientID => {
      const patient = patients.find(p => p.id === patientID);
      if (patient) {
        patientMap.set(patientID, patient);
      }
    });

    // Render with compact PatientCard dividers
    return Object.entries(appointmentsByPatient).map(([patientID, patientAppointments]) => {
      const patient = patientMap.get(patientID);

      // Sort appointments by createdAt date
      const sortedAppointments = patientAppointments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      return (
        <div key={patientID}>
          <PatientCard
            patient={patient}
            link={true}
            compact={true}
          />
          {sortedAppointments.map((appointment) => {
            return (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                users={physicians}
                settings={settings}
              />
            );
          })}
          <div className="pb-3"></div>
          <div className="pb-3"></div>
        </div>
      );
    });
  }

  function renderAssessments(activeAssessments) {
    // Apply deadline filter
    if (deadlineFilter === "delayed") {
      const now = new Date();
      activeAssessments = activeAssessments.filter((assessment) => {
        if (!assessment.deadlineAt) return false;
        const deadline = new Date(assessment.deadlineAt);
        return deadline < now;
      });
    } else if (deadlineFilter === "coming-soon") {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      activeAssessments = activeAssessments.filter((assessment) => {
        if (!assessment.deadlineAt) return false;
        const deadline = new Date(assessment.deadlineAt);
        return deadline >= now && deadline <= sevenDaysFromNow;
      });
    }

    if (activeAssessments.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No assessments</div>
        </div>
      );
    }

    // Group assessments by patientID
    const assessmentsByPatient = activeAssessments.reduce((acc, assessment) => {
      const patientID = assessment.patientID;
      if (!acc[patientID]) acc[patientID] = [];
      acc[patientID].push(assessment);
      return acc;
    }, {});

    // Create patient lookup map from unique patientIDs in assessments
    const uniquePatientIDs = Object.keys(assessmentsByPatient);
    const patientMap = new Map();
    uniquePatientIDs.forEach(patientID => {
      const patient = patients.find(p => p.id === patientID);
      if (patient) {
        patientMap.set(patientID, patient);
      }
    });

    // Render with compact PatientCard dividers
    return Object.entries(assessmentsByPatient).map(([patientID, patientAssessments]) => {
      const patient = patientMap.get(patientID);

      // Sort assessments by createdAt date
      const sortedAssessments = patientAssessments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      return (
        <div key={patientID}>
          <PatientCard
            patient={patient}
            link={true}
            compact={true}
          />
          {sortedAssessments.map((assessment) => {
            return (
              <AssessmentCard
                key={assessment.id}
                assessment={assessment}
                users={physicians}
                settings={settings}
              />
            );
          })}
          <div className="pb-3"></div>
          <div className="pb-3"></div>
        </div>
      );
    });
  }

  // Month View
  function renderCalendarMonth() {

    const today = monthViewDate;

    console.log("Month View Date:", monthViewDate);

    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(todayMidnight.getTime() + 24 * 60 * 60 * 1000 - 1);    

    console.log("Today midnight:", todayMidnight);
    console.log("Today end:", todayEnd);

    let tasksList = tasks.filter((task) => InRangeUTC(new Date(task.deadlineAt), todayMidnight, todayEnd) && task.statusType !== "COMPLETED" && task.statusType !== "CANCELED" );
    let appointmentsList = appointments.filter((appointment) => InRangeUTC(new Date(appointment.deadlineAt), todayMidnight, todayEnd) && appointment.statusType !== "COMPLETED" && appointment.statusType !== "CANCELED" );
    let assessmentsList = assessments.filter((assessment) => InRangeUTC(new Date(assessment.deadlineAt), todayMidnight, todayEnd) && assessment.statusType !== "COMPLETED" && assessment.statusType !== "CANCELED" );
    
    return (
      <>
        <div className="d-flex justify-content-between align-items-top mb-4">
          <div className="pe-4">
            <div className="card">
              <div className="card-content sm">
                <DateSelector date={currentPeriodDateStart} dateSelected={(date) => setMonthViewDate(date)} />
              </div>
            </div>
          </div>
          <div className="ps-4 w-100">
            
            <div className="d-flex justify-content-end align-items-center mb-4">
                    
              <div className={"card clickable w-100 me-2 " + (monthViewTab === "tasks" ? "blue" : "")} onClick={() => setMonthViewTab("tasks")}>
                <div className="card-content sm">
                  <div className="d-flex justify-content-start align-items-center">
                    <div className="typo-strong-300 me-2">{tasksList.length}</div>
                    <div className="typo-normal-175">Tasks</div>
                  </div>
                </div>
              </div>

              <div className={"card clickable w-100 me-2 " + (monthViewTab === "appointments" ? "blue" : "")} onClick={() => setMonthViewTab("appointments")}>
                <div className="card-content sm">
                  <div className="d-flex justify-content-start align-items-center">
                    <div className="typo-strong-300 me-2">{appointmentsList.length}</div>
                    <div className="typo-normal-175">Appointments</div>
                  </div>
                </div>
              </div>

              <div className={"card clickable w-100 " + (monthViewTab === "assessments" ? "blue" : "")} onClick={() => setMonthViewTab("assessments")}>
                <div className="card-content sm">
                  <div className="d-flex justify-content-start align-items-center">
                    <div className="typo-strong-300 me-2">{assessmentsList.length}</div>
                    <div className="typo-normal-175">Assessments</div>
                  </div>
                </div>
              </div>
            </div>

            {monthViewTab === "tasks" ? renderTasks(tasksList) :
             monthViewTab === "appointments" ? renderAppointments(appointmentsList) :
             renderAssessments(assessmentsList)}
          </div>
        </div>

      </>
    );
  }

  if (loading) {
    return (
      <></>
    );
  }

  return (
    <>
      <Helmet title="Dashboard" />

      <div className="container">

        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-start align-items-center">
                <span className="typo-hero-small">Hello, {context.name.split(' ')[0]}</span>
                <span className="typo-normal-175 mt-2 ms-3">This is an overview of your work</span>
              </div>
              <div className="d-flex justify-content-end align-items-center">
                <div className={"item typo-normal-150 ms-2 " + (activeView === "table" ? "grey" : "")} onClick={() => setActiveView("table")}>Table</div>
                <div className={"item typo-normal-150 ms-2 " + (activeView === "month" ? "grey" : "")} onClick={() => setActiveView("month")}>Month</div>
              </div>
            </div>
          </div>
        </div>

        {context.features?.claims && <NavigatorMetrics />}

        {activeView === "table" ? (
          <>
            <div className="row mb-4">
              <div className="col-xs-12">
                <div className="d-flex justify-content-start align-items-center">
                  {renderProjectsSummaryCard()}
                  {renderTasksSummaryCard()}
                  {renderAppointmentsSummaryCard()}
                  {renderAssessmentsSummaryCard()}
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-xs-12">
                {renderFilterButtons()}
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-xs-12">
                {
                  activeTab === "projects" ? renderProjects() :
                  activeTab === "tasks" ? renderTasks(tasks.filter((task) => task.statusType !== "COMPLETED" && task.statusType !== "CANCELED")) :
                  activeTab === "appointments" ? renderAppointments(appointments.filter((appointment) => appointment.statusType !== "COMPLETED" && appointment.statusType !== "CANCELED")) :
                  activeTab === "assessments" ? renderAssessments(assessments.filter((assessment) => assessment.statusType !== "COMPLETED" && assessment.statusType !== "CANCELED")) :
                  renderProjects()
                }
              </div>
            </div>
          </>
        ) : (
          renderCalendarMonth()
        )}
      </div>
    </>
  );
}

export default Dashboard;
